import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tooltip,
} from '@ads/components-react';
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Header,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClassroomDisciplineListItemViewModel } from '@/shared/api/generated';
import { useIsOverflowing } from '@/shared/lib/hooks/useIsOverflowing';
import { Skeleton } from '@/shared/ui/atoms';

import { CLASS_PLANNING_PAGE_SIZE } from '../../../hooks/useClassPlanningList';
import type { ClassPlanningSortField } from '../../../types/classPlanningFilters';
import {
  formatClassPlanningDate,
  formatClassPlanningText,
  formatClassPlanningTimeRange,
} from '../../../utils/classPlanningFormat';
import {
  CLASS_PLANNING_MODULE_INDICATOR_COLOR,
  CLASS_PLANNING_STATUS_DOT_COLOR,
} from '../../../utils/classPlanningStatus';

/**
 * Larguras das colunas: checkbox, Status, Data, Disciplina, Turma, Professor,
 * Módulo, Ação, Editar, Excluir (coluna Documento removida — Rodada 4).
 *
 * Disciplina/Turma/Professor são flexíveis (`minmax(...)`) em vez de largura
 * fixa — em telas ultra-wide o espaço extra se distribui entre as 3 (proporção
 * 2:1:1) em vez de ficar todo concentrado (e "perdido" visualmente) só na
 * Disciplina.
 */
const COLUMNS =
  'grid grid-cols-[40px_48px_110px_minmax(200px,2fr)_minmax(160px,1fr)_minmax(160px,1fr)_40px_48px_40px_40px] items-center gap-xs px-mini py-xxs';

/** Largura mínima do grid — abaixo disso rola horizontalmente em vez de sobrepor colunas. */
const TABLE_MIN_WIDTH = 'min-w-[1200px]';

/** Cabeçalho de coluna com indicador de ordenação clicável (mesmo padrão de `RevisionTable`). */
function SortableHeader({
  header,
  label,
  centered = false,
}: {
  header: Header<ClassroomDisciplineListItemViewModel, unknown>;
  label: ReactNode;
  centered?: boolean;
}) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();

  if (!canSort) {
    return <span className="ts-subtitle-xxs text-ctx-content-base">{label}</span>;
  }

  const Icon =
    sorted === 'asc' ? ChevronUpIcon : sorted === 'desc' ? ChevronDownIcon : ChevronUpDownIcon;

  return (
    <button
      type="button"
      onClick={header.column.getToggleSortingHandler()}
      className={`flex items-center gap-quark ts-subtitle-xxs text-ctx-content-base ${centered ? 'w-full justify-center' : ''}`}
    >
      {label}
      <Icon
        className={`h-micro w-micro shrink-0 ${sorted ? 'text-ctx-content-base' : 'text-ctx-content-placeholder'}`}
      />
    </button>
  );
}

/**
 * Texto truncado com `Tooltip` mostrando o valor completo — só quando o texto
 * está de fato cortado (`isOverflowing`); texto que cabe inteiro não precisa
 * de tooltip.
 */
function TruncatedCell({ value, className }: { value: string; className: string }) {
  const { ref, isOverflowing } = useIsOverflowing<HTMLSpanElement>();

  if (value === '') return null;

  const content = (
    <span ref={ref} className={`block truncate ${className}`}>
      {value}
    </span>
  );

  return isOverflowing ? (
    <Tooltip title={value} side="top" align="start">
      {content}
    </Tooltip>
  ) : (
    content
  );
}

export interface ClassPlanningTableProps {
  readonly data: readonly ClassroomDisciplineListItemViewModel[];
  readonly isLoading: boolean;
  /** Página atual (1-indexed). */
  readonly currentPage: number;
  /** Há uma próxima página (inferido por página cheia quando não há total). */
  readonly hasNextPage: boolean;
  /** Total de páginas, quando o backend informa (envelope paginado). */
  readonly totalPages?: number | undefined;
  /** Total de registros, quando o backend informa (envelope paginado). */
  readonly totalCount?: number | undefined;
  readonly onPageChange: (page: number) => void;
  /** Coluna usada para ordenação (`null` = ordem padrão do backend). */
  readonly sortField: ClassPlanningSortField | null;
  readonly sortDirection: 'asc' | 'desc';
  /** Notifica a nova ordenação (`field: null` quando o usuário remove a ordenação). */
  readonly onSortChange: (field: ClassPlanningSortField | null, direction: 'asc' | 'desc') => void;
}

/**
 * Tabela de planejamento de aulas, no mesmo padrão visual de `RevisionTable`
 * (`exam-revision`): grid CSS com cabeçalhos ordenáveis (Data, Disciplina,
 * Status) e paginação "Exibindo X–Y de Z" integrada ao próprio card — não usa o
 * `DataTable` genérico compartilhado (decisão registrada em `design.md` da
 * change `class-planning-listing-adjustments`).
 *
 * Checkbox de seleção, itens do dropdown de Ação e os ícones de Editar/Excluir
 * continuam sem efeito funcional nesta entrega — só o checkbox é interativo
 * (marca/desmarca visualmente); o resto é `disabled`, sem `onClick`.
 */
const CENTERED_COLUMN_IDS = new Set(['dateStart']);

export function ClassPlanningTable({
  data,
  isLoading,
  currentPage,
  hasNextPage,
  totalPages,
  totalCount,
  onPageChange,
  sortField,
  sortDirection,
  onSortChange,
}: ClassPlanningTableProps) {
  const { t } = useTranslation('classPlanning');

  const sorting: SortingState = sortField
    ? [{ id: sortField, desc: sortDirection === 'desc' }]
    : [];

  const columns = useMemo<ColumnDef<ClassroomDisciplineListItemViewModel>[]>(
    () => [
      {
        id: 'checkbox',
        enableSorting: false,
        header: () => <Checkbox size="md" aria-label={t('table.columns.checkbox')} />,
        cell: () => <Checkbox size="md" aria-label={t('table.columns.checkbox')} />,
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: true,
        header: t('table.columns.status'),
        cell: ({ row }) => {
          const { status } = row.original;
          if (status === undefined) return null;
          return (
            <span
              aria-hidden
              className={`inline-block h-mini w-mini shrink-0 rounded-pill ${CLASS_PLANNING_STATUS_DOT_COLOR[status]}`}
            />
          );
        },
      },
      {
        id: 'dateStart',
        accessorKey: 'dateStart',
        enableSorting: true,
        header: t('table.columns.date'),
        cell: ({ row }) => {
          const timeRange = formatClassPlanningTimeRange(
            row.original.dateStart,
            row.original.dateFinish,
          );
          return (
            <div className="flex flex-col items-center text-center">
              <span className="ts-paragraph-xxs text-ctx-content-base">
                {formatClassPlanningDate(row.original.dateStart)}
              </span>
              {timeRange !== '' && (
                <span className="ts-caption-xxs text-ctx-content-placeholder">{timeRange}</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'disciplineName',
        accessorKey: 'disciplineName',
        enableSorting: true,
        header: t('table.columns.discipline'),
        cell: ({ row }) => (
          <TruncatedCell
            value={formatClassPlanningText(row.original.disciplineName)}
            className="pr-mini ts-subtitle-xxs text-ctx-content-title"
          />
        ),
      },
      {
        id: 'classStudent',
        enableSorting: false,
        header: t('table.columns.classStudent'),
        cell: ({ row }) => (
          <TruncatedCell
            value={formatClassPlanningText(row.original.classStudentName)}
            className="ts-paragraph-xxs text-ctx-content-base"
          />
        ),
      },
      {
        id: 'teacher',
        enableSorting: false,
        header: t('table.columns.teacher'),
        cell: ({ row }) => (
          <TruncatedCell
            value={formatClassPlanningText(row.original.teacherName)}
            className="ts-paragraph-xxs text-ctx-content-base"
          />
        ),
      },
      {
        id: 'module',
        enableSorting: false,
        header: t('table.columns.module'),
        cell: ({ row }) => {
          const { classModuleId, classModuleName } = row.original;
          const hasModule =
            classModuleId !== null && classModuleId !== undefined && !!classModuleName;
          if (!hasModule) return null;
          return (
            <span
              aria-hidden
              className={`inline-block h-mini w-mini shrink-0 rounded-pill ${CLASS_PLANNING_MODULE_INDICATOR_COLOR}`}
            />
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: t('table.columns.actions'),
        cell: () => (
          <Dropdown
            trigger={
              <IconButton
                icon={EllipsisVerticalIcon}
                ariaLabel={t('table.columns.actions')}
                hierarchy="tertiary"
                size="sm"
              />
            }
            align="end"
          >
            <DropdownItem disabled>
              <DropdownItemContent leadingIcon={VideoCameraIcon}>
                {t('actions.liveClasses')}
              </DropdownItemContent>
            </DropdownItem>
            <DropdownItem disabled>
              <DropdownItemContent leadingIcon={ChatBubbleLeftRightIcon}>
                {t('actions.discussions')}
              </DropdownItemContent>
            </DropdownItem>
          </Dropdown>
        ),
      },
      {
        id: 'edit',
        enableSorting: false,
        header: t('table.columns.edit'),
        cell: () => (
          <IconButton
            icon={PencilSquareIcon}
            ariaLabel={t('actions.edit')}
            hierarchy="tertiary"
            size="sm"
            disabled
          />
        ),
      },
      {
        id: 'delete',
        enableSorting: false,
        header: t('table.columns.delete'),
        cell: () => (
          <IconButton
            icon={TrashIcon}
            ariaLabel={t('actions.delete')}
            hierarchy="tertiary"
            size="sm"
            disabled
          />
        ),
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: data as ClassroomDisciplineListItemViewModel[],
    columns,
    state: { sorting },
    manualSorting: true,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const [nextSort] = next;
      if (!nextSort) {
        onSortChange(null, 'asc');
        return;
      }
      onSortChange(nextSort.id as ClassPlanningSortField, nextSort.desc ? 'desc' : 'asc');
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const from = data.length === 0 ? 0 : (currentPage - 1) * CLASS_PLANNING_PAGE_SIZE + 1;
  const to = (currentPage - 1) * CLASS_PLANNING_PAGE_SIZE + data.length;
  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = totalPages !== undefined ? currentPage >= totalPages : !hasNextPage;
  const { ref: scrollRef, isOverflowing: isScrollable } = useIsOverflowing<HTMLDivElement>();

  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
      {/* Grid com rolagem horizontal — nunca sobrepõe colunas em telas estreitas (padrão do legado) */}
      <div className="relative">
        <div ref={scrollRef} className="overflow-x-auto">
          <div className={TABLE_MIN_WIDTH}>
            {/* Header */}
            {table.getHeaderGroups().map(headerGroup => (
              <div
                key={headerGroup.id}
                className={`${COLUMNS} border-b-hairline border-ctx-layout-border`}
              >
                {headerGroup.headers.map(header => (
                  <SortableHeader
                    key={header.id}
                    header={header}
                    label={flexRender(header.column.columnDef.header, header.getContext())}
                    centered={CENTERED_COLUMN_IDS.has(header.column.id)}
                  />
                ))}
              </div>
            ))}

            {/* Body */}
            {isLoading ? (
              <div className="flex flex-col">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className={`${COLUMNS} border-b-hairline border-ctx-layout-border`}>
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-3/4" />
                    <Skeleton className="h-mini w-2/3" />
                    <Skeleton className="h-mini w-1/2" />
                    <Skeleton className="h-mini w-1/2" />
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-full" />
                  </div>
                ))}
              </div>
            ) : data.length === 0 ? (
              <div className="flex items-center justify-center py-xl">
                <span className="ts-paragraph-xxs text-ctx-content-placeholder">
                  {t('table.empty')}
                </span>
              </div>
            ) : (
              table.getRowModel().rows.map(row => (
                <div
                  key={row.id}
                  className={`${COLUMNS} h-[4.5rem] border-b-hairline border-ctx-layout-border bg-ctx-layout-surface`}
                >
                  {row.getVisibleCells().map(cell => (
                    <div key={cell.id} className="min-w-0 overflow-hidden">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Fade no canto direito — sinaliza que há mais conteúdo pra rolar (usuário nem sempre
            percebe que a tabela é arrastável só pelo scrollbar nativo/overlay do navegador). */}
        {isScrollable && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-ctx-layout-surface to-transparent"
          />
        )}
      </div>

      {/* Rodapé de paginação integrado ao card da tabela */}
      <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border px-mini py-micro">
        <span className="ts-paragraph-xxs text-ctx-content-placeholder">
          {totalCount !== undefined
            ? t('table.pagination.showing', { from, to, total: totalCount })
            : t('table.pagination.showingUnknown', { from, to })}
        </span>
        <div className="flex items-center gap-nano">
          <Button
            hierarchy="secondary"
            size="sm"
            leadingIcon={ChevronLeftIcon}
            disabled={isPrevDisabled}
            onClick={() => onPageChange(currentPage - 1)}
          >
            {t('table.pagination.previous')}
          </Button>
          <span className="ts-subtitle-xxs text-ctx-content-base">
            {totalPages !== undefined
              ? t('table.pagination.pageIndicator', { current: currentPage, total: totalPages })
              : t('table.pagination.pageIndicatorUnknown', { current: currentPage })}
          </span>
          <Button
            hierarchy="secondary"
            size="sm"
            trailingIcon={ChevronRightIcon}
            disabled={isNextDisabled}
            onClick={() => onPageChange(currentPage + 1)}
          >
            {t('table.pagination.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
